import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { Assignment, UserEnrollment } from '../../types'
import * as assignmentAPI from '../../api/assignments'

interface AssignmentState {
  assignments: Assignment[]
  userEnrollments: UserEnrollment[]
  currentAssignment: Assignment | null
  currentEnrollment: UserEnrollment | null
  isLoading: boolean
  enrollmentsLoaded: boolean
  error: string | null
}

const initialState: AssignmentState = {
  assignments: [],
  userEnrollments: [],
  currentAssignment: null,
  currentEnrollment: null,
  isLoading: false,
  enrollmentsLoaded: false,
  error: null,
}

// Async thunks
export const fetchAssignments = createAsyncThunk(
  'assignments/fetchAssignments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await assignmentAPI.getAssignments()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assignments')
    }
  }
)

export const fetchUserEnrollments = createAsyncThunk(
  'assignments/fetchUserEnrollments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await assignmentAPI.getUserEnrollments()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user enrollments')
    }
  }
)

export const fetchMyActiveAssignments = createAsyncThunk(
  'assignments/fetchMyActiveAssignments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await assignmentAPI.getMyActiveAssignments()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active assignments')
    }
  }
)

export const fetchMyCompletedAssignments = createAsyncThunk(
  'assignments/fetchMyCompletedAssignments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await assignmentAPI.getMyCompletedAssignments()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch completed assignments')
    }
  }
)

export const startAssignment = createAsyncThunk(
  'assignments/startAssignment',
  async (enrollmentId: string, { rejectWithValue }) => {
    try {
      const response = await assignmentAPI.startAssignment(enrollmentId)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start assignment')
    }
  }
)

export const completeAssignment = createAsyncThunk(
  'assignments/completeAssignment',
  async (enrollmentId: string, { rejectWithValue }) => {
    try {
      const response = await assignmentAPI.completeAssignment(enrollmentId)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to complete assignment')
    }
  }
)

const assignmentSlice = createSlice({
  name: 'assignments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentAssignment: (state, action: PayloadAction<Assignment | null>) => {
      state.currentAssignment = action.payload
    },
    setCurrentEnrollment: (state, action: PayloadAction<UserEnrollment | null>) => {
      state.currentEnrollment = action.payload
    },
    updateEnrollmentProgress: (state, action: PayloadAction<{ enrollmentId: string; progress: number }>) => {
      const enrollment = state.userEnrollments.find(e => e?.id === parseInt(action.payload.enrollmentId))
      if (enrollment) {
        enrollment.progress = action.payload.progress
        if (action.payload.progress >= 100) {
          enrollment.status = 'completed'
          enrollment.completed_at = new Date().toISOString()
        } else if (action.payload.progress > 0 && enrollment.status === 'assigned') {
          enrollment.status = 'in_progress'
          enrollment.started_at = new Date().toISOString()
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Assignments
    builder
      .addCase(fetchAssignments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.isLoading = false
        state.assignments = action.payload
        state.error = null
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch User Enrollments
    builder
      .addCase(fetchUserEnrollments.pending, (state) => {
        state.isLoading = true
        state.enrollmentsLoaded = false
        state.error = null
      })
      .addCase(fetchUserEnrollments.fulfilled, (state, action) => {
        state.isLoading = false
        state.enrollmentsLoaded = true
        state.userEnrollments = action.payload
        state.error = null
      })
      .addCase(fetchUserEnrollments.rejected, (state, action) => {
        state.isLoading = false
        state.enrollmentsLoaded = true  // Mark as loaded even on error to prevent infinite loading
        state.error = action.payload as string
      })

    // Fetch My Active Assignments
    builder
      .addCase(fetchMyActiveAssignments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchMyActiveAssignments.fulfilled, (state, action) => {
        state.isLoading = false
        // Update userEnrollments with active assignments
        const activeEnrollments = action.payload
        state.userEnrollments = state.userEnrollments.filter(e => 
          !activeEnrollments.find(ae => ae?.id === e?.id)
        ).concat(activeEnrollments)
        state.error = null
      })
      .addCase(fetchMyActiveAssignments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch My Completed Assignments
    builder
      .addCase(fetchMyCompletedAssignments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchMyCompletedAssignments.fulfilled, (state, action) => {
        state.isLoading = false
        // Update userEnrollments with completed assignments
        const completedEnrollments = action.payload
        state.userEnrollments = state.userEnrollments.filter(e => 
          !completedEnrollments.find(ce => ce?.id === e?.id)
        ).concat(completedEnrollments)
        state.error = null
      })
      .addCase(fetchMyCompletedAssignments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Start Assignment
    builder
      .addCase(startAssignment.pending, (state) => {
        state.error = null
      })
      .addCase(startAssignment.fulfilled, (state, action) => {
        // Update the enrollment in the state
        const updatedEnrollment = action.payload
        if (updatedEnrollment && updatedEnrollment.id) {
          const index = state.userEnrollments.findIndex(e => e?.id === updatedEnrollment.id)
          if (index !== -1) {
            state.userEnrollments[index] = updatedEnrollment
          }
        }
        state.error = null
      })
      .addCase(startAssignment.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Complete Assignment
    builder
      .addCase(completeAssignment.pending, (state) => {
        state.error = null
      })
      .addCase(completeAssignment.fulfilled, (state, action) => {
        // Update the enrollment in the state
        const updatedEnrollment = action.payload
        if (updatedEnrollment && updatedEnrollment.id) {
          const index = state.userEnrollments.findIndex(e => e?.id === updatedEnrollment.id)
          if (index !== -1) {
            state.userEnrollments[index] = updatedEnrollment
          }
        }
        state.error = null
      })
      .addCase(completeAssignment.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const { 
  clearError, 
  setCurrentAssignment, 
  setCurrentEnrollment, 
  updateEnrollmentProgress 
} = assignmentSlice.actions

export default assignmentSlice.reducer
